import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Minus, Plus, Lock } from 'lucide-react';

// One ceiling decides the control: a fixed value at 1, a stepper for small caps,
// a number field with a hard max above that, and free entry when nothing caps it.
export function QuantityField({ value, onChange, max, loading, testId }) {
  const capped = typeof max === 'number';
  const num = parseInt(value, 10);
  const over = capped && !Number.isNaN(num) && num > max;

  if (loading) {
    return <div data-testid={`${testId}-loading`} className="h-11 flex items-center text-xs text-gray-400">…</div>;
  }

  if (capped && max === 0) {
    return (
      <div data-testid={`${testId}-none-left`} className="h-11 flex items-center gap-1.5 text-xs font-medium text-red-600">
        <Lock size={13} /> None left
      </div>
    );
  }

  if (capped && max === 1) {
    return (
      <div data-testid={`${testId}-fixed`} className="h-11 flex items-center gap-2">
        <span className="tabular-nums text-base font-medium text-[var(--ink)]">1</span>
        <span className="text-[11px] text-gray-400">limited to 1</span>
      </div>
    );
  }

  if (capped && max <= 10) {
    const step = (delta) => {
      const next = Math.min(Math.max((Number.isNaN(num) ? 1 : num) + delta, 1), max);
      onChange(String(next));
    };
    return (
      <div data-testid={`${testId}-stepper`} className="h-11 flex items-center gap-1">
        <Button type="button" variant="outline" size="icon" data-testid={`${testId}-minus`}
          className="h-9 w-9 shrink-0" disabled={num <= 1} onClick={() => step(-1)}>
          <Minus size={14} />
        </Button>
        <span className="w-10 text-center tabular-nums text-base font-medium">{Number.isNaN(num) ? 1 : num}</span>
        <Button type="button" variant="outline" size="icon" data-testid={`${testId}-plus`}
          className="h-9 w-9 shrink-0" disabled={num >= max} onClick={() => step(1)}>
          <Plus size={14} />
        </Button>
        <span className="text-[11px] text-gray-400 ms-1">of {max}</span>
      </div>
    );
  }

  return (
    <div>
      <Input
        type="number" min="1" max={capped ? max : undefined} value={value} data-testid={testId}
        onChange={e => onChange(e.target.value)}
        className={`bg-white border-gray-300 h-11 text-base ${over ? 'border-red-400' : ''}`}
      />
      {over && (
        <p data-testid={`${testId}-error`} className="text-[11px] text-red-600 mt-1">Max {max} for this match</p>
      )}
      {!over && capped && (
        <p className="text-[11px] text-gray-400 mt-1 tabular-nums">max {max}</p>
      )}
    </div>
  );
}
